/**
 * Panel displaying task logs with expandable metadata.
 */
import type { FC } from "react";

import type { TaskLogsResult } from "../../../hooks/useTaskLogs";

import { formatDateTime } from "../../../utils/time";
import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";
import { EmptyState } from "../../common/EmptyState";
import { ChevronRightIcon } from "../../common/icons";
import { ListItemCard, ListItemCardMetadata } from "../../common/ListItemCard";
import { LoadingState } from "../../common/LoadingState";
import { SectionHeader } from "../../common/SectionHeader";
import { Text } from "../../common/Text";
import { LEVEL_BADGE_CLASS } from "../constants";

interface TaskLogsPanelProps {
  logs: TaskLogsResult;
}

export const TaskLogsPanel: FC<TaskLogsPanelProps> = ({ logs }) => {
  const renderContent = () => {
    if (logs.loading) {
      return <LoadingState message="Loading task logs..." className="py-8" />;
    }

    if (logs.error && !logs.data) {
      return (
        <Alert
          variant="error"
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={logs.reload}
              className="border-rose-400/50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/60 dark:text-rose-200 dark:hover:bg-rose-500/20"
            >
              Retry
            </Button>
          }
        >
          {logs.error}
        </Alert>
      );
    }

    if (!logs.data || logs.data.length === 0) {
      return (
        <EmptyState
          title="No logs captured"
          description="No logs have been captured for this task yet."
          size="sm"
          className="!py-8"
        />
      );
    }

    return (
      <div className="custom-scrollbar max-h-[50vh] space-y-2 overflow-y-auto pr-1">
        {logs.data.map((log) => (
          <ListItemCard
            key={log.id}
            className="hover:!border-arti-primary/30 dark:hover:!border-arti-primary/30 group !border-gray-200 !bg-white transition-all hover:shadow-sm dark:!border-white/10 dark:!bg-white/5"
            header={
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs text-gray-400 dark:text-gray-500">
                  {formatDateTime(log.created_at)}
                </span>
                <span
                  className={`inline-flex rounded px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider ${LEVEL_BADGE_CLASS[log.level]}`}
                >
                  {log.level}
                </span>
              </div>
            }
            footer={
              <Text
                as="div"
                className="flex flex-wrap items-center gap-x-3 gap-y-1"
              >
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-white/10">
                  {log.component}
                  {log.scope ? `:${log.scope}` : ""}
                </span>
                {log.node_id && <span>node: {log.node_id}</span>}
                {log.correlation_id && (
                  <span className="font-mono opacity-75">
                    corr: {log.correlation_id.slice(0, 8)}...
                  </span>
                )}
              </Text>
            }
          >
            <div className="break-words font-mono text-sm leading-relaxed text-gray-700 dark:text-gray-200">
              {log.message}
            </div>
            {log.metadata && (
              <details className="group/meta mt-2">
                <summary className="flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <ChevronRightIcon
                    size={12}
                    className="h-3 w-3 transition-transform group-open/meta:rotate-90"
                    strokeWidth={1.5}
                  />
                  View Metadata
                </summary>
                <div className="mt-2">
                  <ListItemCardMetadata
                    data={log.metadata}
                    className="!bg-gray-50 dark:!bg-black/30"
                  />
                </div>
              </details>
            )}
          </ListItemCard>
        ))}
      </div>
    );
  };

  return (
    <section>
      <SectionHeader
        title="Task Logs"
        variant="label"
        icon={
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        }
      />
      {renderContent()}
    </section>
  );
};
